import { useApolloClient } from '@apollo/client';
import { isEmpty, isNil, omit } from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Fragments } from '@shared/api';
import { API } from '@shared/types/api';
import { notNil } from '@shared/util/notNil';

export type LocalChangeType =
  | 'WORKFLOW_ENTITY_CREATED'
  | 'WORKFLOW_ENTITY_UPDATED'
  | 'WORKFLOW_ENTITY_DELETED'
  | 'WORKFLOW_ENTITY_RESTORED';

/**
 * A change this client made to cards, known from its own mutation. Card lists apply it as soon as
 * the mutation returns rather than waiting for the server's event, which then finds it applied.
 */
export interface LocalWorkflowEntityChange {
  type: LocalChangeType;
  // the cards as the change left them - a deleted card as it was, with `deletedAt` set
  entities: API.WorkflowEntity[];
  // the cards as the client had them before the change, for those it had
  previousEntities: API.WorkflowEntity[];
  refs?: API.EntityRefsFragment | null;
}

type LocalChangeListener = (change: LocalWorkflowEntityChange) => void;

// Kept module-wide rather than in context: a change made in the card modal has to reach the lists
// of the collection behind it, which sit under another provider.
const listeners = new Set<LocalChangeListener>();

/** Subscribes onChange to the changes this client makes. The latest onChange is always invoked. */
export function useLocalWorkflowEntityChange(onChange: LocalChangeListener) {
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    const listener: LocalChangeListener = change => onChangeRef.current(change);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);
}

interface LocalWorkflowEntityChanges {
  // the cards as the cache holds them, read before a mutation replaces them
  readCards(ids: string[]): API.WorkflowEntity[];
  publish(change: LocalWorkflowEntityChange): void;
}

/**
 * For a mutation hook: reads the cards it's about to change, and publishes the change once it
 * returns. Every list writes inside one cache batch, so the change broadcasts once.
 */
export function useLocalWorkflowEntityChanges(): LocalWorkflowEntityChanges {
  const client = useApolloClient();

  const readCards = useCallback(
    (ids: string[]) =>
      ids
        .map(id =>
          client.cache.readFragment<API.WorkflowEntity>({
            id: client.cache.identify({ __typename: 'WorkflowEntity', id }),
            fragment: Fragments.workflow.WorkflowEntity,
          }),
        )
        .filter(notNil),
    [client],
  );

  const publish = useCallback(
    (change: LocalWorkflowEntityChange) => {
      if (isEmpty(change.entities)) {
        return;
      }
      client.cache.batch({
        update: () => listeners.forEach(listener => listener(change)),
      });
    },
    [client],
  );

  return useMemo(() => ({ readCards, publish }), [readCards, publish]);
}

/** A card as deleting it leaves it: unchanged but for `deletedAt`, as the server's event has it. */
export function toDeletedCard(card: API.WorkflowEntity, deletedAt: string): API.WorkflowEntity {
  return { ...card, fields: { ...card.fields, deletedAt } };
}

/** A card as restoring it leaves it: unchanged but for `deletedAt`, which the server removes. */
export function toRestoredCard(card: API.WorkflowEntity): API.WorkflowEntity {
  return { ...card, fields: isNil(card.fields) ? card.fields : omit(card.fields, 'deletedAt') };
}
